import {
  type SugarPublishedTuitionRate,
  type SugarPublishedTuitionRateCurrent,
  type SugarPublishedTuitionRatesUpsert
} from '@app/types'
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Collapse,
  Group,
  Paper,
  Radio,
  Stack,
  Text,
  Title,
  UnstyledButton
} from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconInfoCircle,
  IconLock,
  IconX
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import minMax from 'dayjs/plugin/minMax'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'

import { ConfirmModal, DateInput, Modal, NumberInput } from '@app/components'
import {
  findProgramYearForDate,
  findScholarshipLockRow,
  useMe,
  useTuitionRatesHistory,
  useUpdateTuitionRates
} from '@app/lib'
import { useSugarContext } from '@app/providers'

dayjs.extend(isSameOrAfter)
dayjs.extend(minMax)

const HARD_FLOOR_DATE = '2026-09-01'

interface FormValues {
  effectiveDate: Date | null
  fourYearRate: number | ''
  ratesDifferThreeYr: boolean
  threeYearRate: number | ''
  scholarshipOptIn: boolean | null
}

const toInitialValues = (openRecord: SugarPublishedTuitionRate | null | undefined): FormValues => ({
  effectiveDate: openRecord?.effectiveDate ? dayjs(openRecord.effectiveDate).toDate() : null,
  fourYearRate: openRecord?.fourYearOld.maxMonthlyRate ?? '',
  ratesDifferThreeYr: openRecord?.ratesDifferThreeYr ?? false,
  threeYearRate: openRecord?.threeYearOld?.maxMonthlyRate ?? '',
  scholarshipOptIn: openRecord?.scholarshipOptIn ?? null
})

const FIELD_MAP: Array<[RegExp, keyof FormValues]> = [
  [/^fourYearOld\.maxMonthlyRate\b/i, 'fourYearRate'],
  [/^threeYearOld\.maxMonthlyRate\b/i, 'threeYearRate'],
  [/^ratesDifferThreeYr\b/i, 'ratesDifferThreeYr'],
  [/^scholarshipOptIn\b/i, 'scholarshipOptIn'],
  [/^effectiveDate\b/i, 'effectiveDate']
]

export interface TuitionRatesModalProps {
  onClose: () => void
  opened: boolean
  // The current-rates shape rather than the plain row: the backdate floor below
  // needs the queued `upcoming` row, which only the no-effectiveDate GET returns.
  openRecord: SugarPublishedTuitionRateCurrent | null
}

export const TuitionRatesModal: FC<TuitionRatesModalProps> = ({ onClose, opened, openRecord }) => {
  const { siteId } = useSugarContext()
  const { data: me } = useMe()
  const currentSite = useMemo(() => me?.sites.find((site) => site.id === siteId), [me?.sites, siteId])
  const programYears = useMemo(() => currentSite?.programYears ?? [], [currentSite?.programYears])
  const { isUpdatingTuitionRates, updateTuitionRates } = useUpdateTuitionRates()
  // Hoisted above validationSchema (rather than left below, next to the rest of the lock-state
  // derivation) because the must-choose test below needs it and has no access to the form
  // instance -- the form's own validate config is built from validationSchema, so validationSchema
  // can never read form.values without a circular dependency.
  const { data: history } = useTuitionRatesHistory()

  // The latest effective date already on file for this site. A queued `upcoming`
  // row sits after the in-effect one, so it -- not openRecord -- is the real
  // backdate floor. The API rejects an earlier date with "Cannot backdate: a
  // record exists with a later effective date.", which matches no FIELD_MAP
  // pattern and so would surface as a top-level alert instead of on this field.
  const latestRecordDate = useMemo(() => {
    const dates = [openRecord?.effectiveDate, openRecord?.upcoming?.effectiveDate]
      .filter((date): date is string => Boolean(date))
      .map((date) => dayjs(date))

    return dates.length > 0 ? dayjs.max(dates) : null
  }, [openRecord?.effectiveDate, openRecord?.upcoming?.effectiveDate])

  const minDate = useMemo(() => {
    const floor = dayjs(HARD_FLOOR_DATE)
    return (latestRecordDate ? dayjs.max(latestRecordDate, floor) : floor).toDate()
  }, [latestRecordDate])

  // MIQ-2924: the date a new record opens on -- the later of the floor above and the
  // start of the program year under way today. Seeding it from the current record
  // instead opened a site still on last year's record at last year's date, below the
  // hard floor, with the form invalid before the provider touched it. For a record
  // already in the current year (or a queued later one) this resolves to that
  // record's own date, since minDate is never earlier than it, so editing keeps it.
  const defaultEffectiveDate = useMemo(() => {
    const currentYear = findProgramYearForDate(programYears, dayjs().format('YYYY-MM-DD'))

    return currentYear ? dayjs.max(dayjs(minDate), dayjs(currentYear.startDate)).toDate() : minDate
  }, [minDate, programYears])

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        effectiveDate: yup
          .date()
          .nullable()
          .required('Effective date is required')
          .test(
            'hard-floor',
            `Effective date cannot be earlier than ${dayjs(HARD_FLOOR_DATE).format('MMM D, YYYY')}`,
            (value) => Boolean(value && dayjs(value).isSameOrAfter(dayjs(HARD_FLOOR_DATE), 'day'))
          )
          .test('within-program-year', 'Effective date does not fall within any Program Year', (value) => {
            if (!value) return false
            const dateStr = dayjs(value).format('YYYY-MM-DD')
            return Boolean(findProgramYearForDate(programYears, dateStr))
          })
          .test(
            'no-backdate',
            latestRecordDate
              ? `Cannot be earlier than the latest rate period's start date (${latestRecordDate.format('MMM D, YYYY')})`
              : '',
            (value) => (latestRecordDate ? Boolean(value && dayjs(value).isSameOrAfter(latestRecordDate, 'day')) : true)
          ),
        fourYearRate: yup.number().typeError('Required').moreThan(0, 'Must be > 0').required('Required'),
        ratesDifferThreeYr: yup.boolean().required(),
        threeYearRate: yup.number().when('ratesDifferThreeYr', {
          is: true,
          then: (schema) => schema.typeError('Required').moreThan(0, 'Must be > 0').required('Required'),
          otherwise: (schema) => schema.transform((val, orig) => (orig === '' ? undefined : val)).optional()
        }),
        scholarshipOptIn: yup
          .boolean()
          .nullable()
          // A locked row's own choice can itself be null -- DPP staff can clear just the value
          // and leave the lock date stamped (see MIQ-2577's staff-override design). The radio is
          // forced to that null from the locked row by the effect below, so this test has to
          // tolerate it too, or isValid() stays false and Save is disabled with no way out. Uses
          // this.parent (a plain function, not an arrow, so `this` binds) rather than the
          // optInLocked flag computed further down: that flag needs form.values.effectiveDate,
          // and form itself is built from this schema, so it can't be a dependency here.
          .test('must-choose', 'Please select Opt In or Opt Out', function (v) {
            if (v === true || v === false) return true

            const parentEffectiveDate = this.parent.effectiveDate
            if (!parentEffectiveDate) return false

            const targetYear = findProgramYearForDate(programYears, dayjs(parentEffectiveDate).format('YYYY-MM-DD'))
            return Boolean(findScholarshipLockRow(history, targetYear?.id)?.scholarshipOptInLocked)
          })
      }),
    [latestRecordDate, programYears, history]
  )

  const [topError, setTopError] = useState<string | null>(null)

  // The row the provider is actually working from. When `upcoming` exists it is
  // the most recent submission on file and supersedes openRecord itself (see
  // the tab's "these rates replace the ones above" messaging), so the form
  // must prefill from it -- otherwise it opens below its own minDate floor
  // (which is computed from latestRecordDate, i.e. this same row) with no
  // field yet touched to surface the resulting validation error.
  const latestOpenRecord = openRecord?.upcoming ?? openRecord

  const form = useForm<FormValues>({
    initialValues: toInitialValues(latestOpenRecord),
    validate: yupResolver(validationSchema),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })
  const [isConfirming, confirmHandlers] = useDisclosure(false)
  const [isMoreInfoOpen, moreInfoHandlers] = useDisclosure(false)

  // The lock is per program year, and the provider chooses which year they are
  // targeting by picking an effective date -- so it has to be resolved from the
  // live form value, not read off openRecord. A provider entering next year's
  // rates for the first time must still be able to choose freely.
  const targetProgramYear = useMemo(
    () =>
      form.values.effectiveDate
        ? findProgramYearForDate(programYears, dayjs(form.values.effectiveDate).format('YYYY-MM-DD'))
        : null,
    [form.values.effectiveDate, programYears]
  )
  const lockRow = useMemo(
    () => findScholarshipLockRow(history, targetProgramYear?.id),
    [history, targetProgramYear?.id]
  )
  const optInLocked = Boolean(lockRow?.scholarshipOptInLocked)

  useEffect(() => {
    const next = { ...toInitialValues(latestOpenRecord), effectiveDate: defaultEffectiveDate }
    // Fold the locked choice into the BASELINE rather than correcting it after
    // the fact. latestOpenRecord can be an older row whose own scholarshipOptIn
    // disagrees with its year's locked value (staff can clear the value and
    // leave the lock date stamped), and on a close/reopen neither dep of the
    // correction effect below changes -- the date returns to latestOpenRecord's
    // and resolves to the same year -- so that effect cannot re-apply the
    // override. Resolved from next.effectiveDate, not latestOpenRecord.programYearId,
    // for the same reason the lock is resolved from the live date further down:
    // the year in play is the one the effective date lands in.
    const openLockRow = findScholarshipLockRow(
      history,
      next.effectiveDate
        ? findProgramYearForDate(programYears, dayjs(next.effectiveDate).format('YYYY-MM-DD'))?.id
        : null
    )
    if (openLockRow?.scholarshipOptInLocked) next.scholarshipOptIn = openLockRow.scholarshipOptIn

    form.setValues(next)
    form.resetDirty(next)
    setTopError(null)
    // defaultEffectiveDate is a dependency because the site's program years can
    // resolve after this modal mounts, and a record that stays null never changes
    // openRecord to re-trigger this. Seeded from an empty programYears list the
    // default falls back to the hard floor, and nothing would move it to the
    // current program year's start once the list arrives. Depended on by its time
    // value rather than the Date itself: a refetch that rebuilds an equivalent
    // programYears array would otherwise hand this effect a new Date holding the
    // same day, and the reseed below would take a provider's unsaved rate edits
    // with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRecord, opened, defaultEffectiveDate.getTime()])

  // Covers only what the baseline above cannot: the provider moving the
  // effective date into a different program year while the modal is open. That
  // change has already dirtied the form via the date itself, so there is no
  // resetDirty here -- resetDirty snapshots the CURRENT values, which would
  // absorb every unsaved rate edit into the baseline and grey out Save with
  // those edits still on screen.
  useEffect(() => {
    if (!optInLocked || !lockRow) return
    if (form.values.scholarshipOptIn === lockRow.scholarshipOptIn) return

    form.setFieldValue('scholarshipOptIn', lockRow.scholarshipOptIn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optInLocked, lockRow?.scholarshipOptIn])

  useEffect(() => {
    if (topError !== null) setTopError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values])

  const handleClose = useCallback(() => {
    if (form.isDirty()) {
      confirmHandlers.open()
    } else {
      onClose()
    }
  }, [form, onClose, confirmHandlers])

  const handleRatesDifferChange = (checked: boolean) => {
    form.setFieldValue('ratesDifferThreeYr', checked)
    if (!checked) form.setFieldValue('threeYearRate', '')
  }

  const handleScholarshipChange = (value: string) => {
    form.setFieldValue('scholarshipOptIn', value === 'true')
  }

  const handleSubmit = async (values: FormValues) => {
    setTopError(null)

    const payload: Omit<SugarPublishedTuitionRatesUpsert, 'siteId'> = {
      effectiveDate: dayjs(values.effectiveDate).format('YYYY-MM-DD'),
      fourYearOld: { maxMonthlyRate: Number(values.fourYearRate) },
      ratesDifferThreeYr: values.ratesDifferThreeYr,
      ...(values.ratesDifferThreeYr ? { threeYearOld: { maxMonthlyRate: Number(values.threeYearRate) } } : {}),
      // Omit rather than resubmit the same value: the API documents an omitted key as
      // the supported shape for a rate-only change on a locked year, which makes a
      // value mismatch structurally impossible.
      ...(optInLocked ? {} : { scholarshipOptIn: values.scholarshipOptIn as boolean })
    }

    try {
      const result = await updateTuitionRates(payload)

      if (result.success) {
        onClose()
        return
      }

      const errMsg =
        result.error?.message ??
        result.messages?.find((m) => m.type === 'error')?.message ??
        'Save failed. Please try again.'

      const match = FIELD_MAP.find(([re]) => re.test(errMsg))

      if (match) {
        form.setFieldError(match[1], errMsg)
        setTopError(null)
      } else {
        setTopError(errMsg)
      }
    } catch {
      setTopError('Save failed. Please try again.')
    }
  }

  const busy = isUpdatingTuitionRates
  // A date with no record on file is a new record, and saving it is the point even
  // when every rate carries over unchanged. The form opens on that date without
  // being dirty (the provider has changed nothing), so the dirty check alone would
  // leave Save disabled until they edited something and changed it back.
  const isNewRecord =
    !latestOpenRecord?.effectiveDate || !dayjs(form.values.effectiveDate).isSame(latestOpenRecord.effectiveDate, 'day')
  // The null check does not apply when locked: the value is forced from the locked
  // row above, and a locked row with a null choice would otherwise disable Save
  // permanently with no control the provider could use to fix it.
  const saveDisabled =
    busy ||
    (!form.isDirty() && !isNewRecord) ||
    !form.isValid() ||
    (!optInLocked && form.values.scholarshipOptIn === null)

  return (
    <>
      <Modal
        closeOnClickOutside
        closeOnEscape
        onClose={handleClose}
        opened={opened && !isConfirming}
        size='lg'
        title='Enter New Tuition Rates'
        zIndex={1001}
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Alert
              color='gray'
              radius='lg'
            >
              <Text size='sm'>
                Please use your site&apos;s HIGHEST MONTHLY published tuition rate to complete the following fields. The
                highest published tuition rate refers to the maximum full, undiscounted rate your program publicly
                advertises or documents in your official tuition schedule (such as your website, program brochure, or
                enrollment packet). This is the rate a private-pay family who enrolls in the maximum number of hours
                offered would be charged prior to any scholarships, sliding scale adjustments, or financial assistance
                being applied.
              </Text>
            </Alert>
            <DateInput
              // MIQ-2343 asked for the derived program year before save; it used to
              // surface only inside the lock and opt-in copy.
              description={
                targetProgramYear && !form.errors.effectiveDate ? `Program Year: ${targetProgramYear.name}` : undefined
              }
              disabled={busy}
              label='Effective Date'
              minDate={minDate}
              withAsterisk
              {...form.getInputProps('effectiveDate')}
            />
            {form.values.effectiveDate && (
              <Alert
                color='blue'
                icon={<IconInfoCircle size={18} />}
                radius='lg'
              >
                Tuition rate changes will be effective the 1st of the effective month. Ex: tuition rates updated on{' '}
                {dayjs(form.values.effectiveDate).format('MM/DD/YYYY')} will become effective on{' '}
                {dayjs(form.values.effectiveDate).startOf('month').format('MM/DD/YYYY')}.
              </Alert>
            )}
            <Paper
              p='md'
              radius='lg'
              withBorder
            >
              <Stack>
                <Title order={4}>Tuition Rates</Title>
                <NumberInput
                  disabled={busy}
                  hideControls
                  icon='$'
                  label='Maximum MONTHLY tuition rate (4-Year-Old Cohort)'
                  min={0.01}
                  placeholder='0.01'
                  precision={2}
                  withAsterisk
                  {...form.getInputProps('fourYearRate')}
                />
                <Checkbox
                  checked={form.values.ratesDifferThreeYr}
                  color='brand-dark-blue'
                  disabled={busy}
                  label='My rates are different for 3-year-old cohort'
                  onChange={(ev) => handleRatesDifferChange(ev.currentTarget.checked)}
                />
                {form.values.ratesDifferThreeYr && (
                  <NumberInput
                    disabled={busy}
                    hideControls
                    icon='$'
                    label='Maximum MONTHLY tuition rate (3-Year-Old Cohort)'
                    min={0.01}
                    placeholder='0.01'
                    precision={2}
                    withAsterisk
                    {...form.getInputProps('threeYearRate')}
                  />
                )}
              </Stack>
            </Paper>
            <Paper
              p='md'
              radius='lg'
              withBorder
            >
              <Stack>
                <Title order={4}>DPP Scholarship Program</Title>
                <Radio.Group
                  label='Indicate whether you would like to opt in as a potential DPP Scholarship-accepting provider.'
                  onChange={handleScholarshipChange}
                  value={form.values.scholarshipOptIn === null ? '' : String(form.values.scholarshipOptIn)}
                  withAsterisk
                  {...(form.errors.scholarshipOptIn ? { error: form.errors.scholarshipOptIn as string } : {})}
                >
                  <Stack
                    mt='xs'
                    spacing='xs'
                  >
                    <Radio
                      color='brand-dark-blue'
                      disabled={busy || optInLocked}
                      label='Opt In'
                      value='true'
                    />
                    <Radio
                      color='brand-dark-blue'
                      disabled={busy || optInLocked}
                      label='Opt Out'
                      value='false'
                    />
                  </Stack>
                </Radio.Group>
                {optInLocked && (
                  <Alert
                    color='gray'
                    icon={<IconLock size={18} />}
                    radius='lg'
                  >
                    <Text size='sm'>
                      Your DPP Scholarship selection
                      {targetProgramYear ? ` for ${targetProgramYear.name}` : ''} was submitted
                      {lockRow?.scholarshipOptInLockedOn
                        ? ` on ${dayjs(lockRow.scholarshipOptInLockedOn).format('MMM D, YYYY')}`
                        : ''}{' '}
                      and can&apos;t be changed here. To change it, contact DPP at info@dpp.org or 303-595-4377.
                    </Text>
                  </Alert>
                )}
                {form.values.scholarshipOptIn === false && (
                  <Text
                    color='dimmed'
                    size='sm'
                  >
                    If you select &lsquo;Opt out&rsquo;, your families will not be eligible to potentially receive the
                    additional DPP Scholarship funds, and will only have access to DPP tuition credits.
                  </Text>
                )}
                <UnstyledButton
                  aria-controls='dpp-scholarship-more-info'
                  aria-expanded={isMoreInfoOpen}
                  onClick={moreInfoHandlers.toggle}
                  type='button'
                >
                  <Group spacing='xs'>
                    <Text
                      fw={600}
                      size='sm'
                    >
                      More Info
                    </Text>
                    <ActionIcon
                      aria-hidden
                      component='span'
                      size='sm'
                      variant='subtle'
                    >
                      {isMoreInfoOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                    </ActionIcon>
                  </Group>
                </UnstyledButton>
                <Collapse
                  id='dpp-scholarship-more-info'
                  in={isMoreInfoOpen}
                >
                  <Stack spacing='sm'>
                    <Text size='sm'>
                      Please review the DPP Tuition Credit Policy regarding DPP Scholarship students and indicate
                      whether you would like to opt in as a potential DPP Scholarship-accepting provider by selecting
                      &lsquo;Opt in.&rsquo; If you opt in, your student roster will be evaluated by DPP staff for
                      potential additional funding, beyond the monthly tuition credit, based on eligibility criteria and
                      funding availability. Your selection will apply to the{' '}
                      {targetProgramYear ? `${targetProgramYear.name} program year` : 'selected program year'} only and
                      will be updated annually. If you select &lsquo;Opt out&rsquo;, your families will not be eligible to
                      potentially receive the additional DPP Scholarship funds, and will only have access to DPP tuition
                      credits. Please contact Chris Miller, DPP VP of Programs, with any questions at Chris@dpp.org.
                    </Text>
                    <Text size='sm'>
                      <Text
                        fw={700}
                        span
                      >
                        Tuition Credit Policy
                      </Text>{' '}
                      <Text
                        fs='italic'
                        span
                      >
                        (from DPP&apos;s 2026-2027 provider agreement)
                      </Text>
                      : For Students awarded a DPP Scholarship who are not receiving Colorado UPK, the total amount a
                      Provider receives on behalf of a Denver Preschool Program (DPP) Student from DPP, combining DPP
                      Tuition Credits and DPP Scholarship support, shall not exceed DPP&apos;s monthly cost of care rate
                      applicable to such child ($1,700 per month for the 2026-2027 school year) or the Provider&apos;s
                      published tuition rate applicable to such child (up to DPP&apos;s Monthly Maximum Published Rate,
                      $1,950 per month for the 2026-2027 school year), whichever is higher. While DPP Scholarship
                      Students can also qualify for Colorado&apos;s Universal Preschool Program (UPK),{' '}
                      <Text
                        fw={700}
                        span
                      >
                        Providers confirm that DPP Scholarship Students do not receive any non-UPK public funding and
                        that families have $0 in monthly out-of-pocket tuition expenses.
                      </Text>{' '}
                      For DPP Scholarship Students who are receiving Colorado UPK, the total amount a Provider receives
                      combined between DPP and UPK cannot exceed the higher of Provider&apos;s published tuition rate or
                      DPP&apos;s Cost of Care, and DPP-only funding cannot exceed DPP&apos;s defined Monthly Maximum
                      Published Rate ($1,950 for the 2026-2027 school year).
                    </Text>
                  </Stack>
                </Collapse>
              </Stack>
            </Paper>
            {topError && (
              <Alert
                color='red'
                icon={<IconAlertCircle size={18} />}
                radius='lg'
                role='alert'
              >
                {topError}
              </Alert>
            )}
            <Group
              mt='xl'
              position='apart'
            >
              <Button
                disabled={busy}
                leftIcon={<IconX size={18} />}
                onClick={handleClose}
                variant='subtle'
              >
                Cancel
              </Button>
              <Button
                color='green.8'
                disabled={saveDisabled}
                leftIcon={<IconDeviceFloppy size={18} />}
                loading={busy}
                type='submit'
              >
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
      <ConfirmModal
        cancelLabel='Oops, Nevermind'
        closeOnConfirm
        confirmLabel="Yes, I'm Sure"
        onClose={confirmHandlers.close}
        onConfirm={() => {
          confirmHandlers.close()
          onClose()
        }}
        opened={isConfirming}
        title='Unsaved Changes'
        zIndex={1002}
      >
        <Text size='sm'>You have unsaved changes, are you sure you want to cancel?</Text>
      </ConfirmModal>
    </>
  )
}
